<script lang="ts">
  /**
   * One challenge everybody plays on the same deal against the clock — a
   * daily, a weekly tournament, an event. A header (a glyph tile, the name,
   * one line of what it is, the time left), the caller's own body (this
   * week's stars, your rank), and a row of actions.
   *
   * The game decides whether a card is shown at all: a card appears only
   * while its challenge runs (the menus proposal, change 3).
   *
   * The time left (since 1.2.0) is either `countdown`, a string the game
   * formatted and keeps fresh, or `endsAt`, the instant it ends: then the
   * card keeps its own clock, ticking at each whole minute and every second
   * in the last minute, formatted by `formatCountdown` (default
   * `formatTimeLeft`: "5d 01h", "7h 12m", "45m", "42s"). `countdown` wins
   * when both are given. `onend` is called once when `endsAt` passes.
   *
   * Test ids: `testid` on the card, `countdownTestid` (default
   * `ui-challenge-countdown`) on the clock.
   */
  import type { Snippet } from 'svelte';

  import { styleProps } from '../styleProps.js';
  import { formatTimeLeft, nextTickIn } from '../time.js';

  interface Props {
    readonly title: string;
    readonly detail?: string | undefined;
    readonly icon: Snippet;
    /** The glyph tile's fill, as any CSS colour or gradient; the accent by default. */
    readonly iconBg?: string | undefined;
    /** Its glyph colour; the on-accent colour by default. */
    readonly iconInk?: string | undefined;
    /** Already-formatted time left ("5d 01h"). Wins over `endsAt`. */
    readonly countdown?: string | undefined;
    /** When the challenge ends (a `Date` or epoch ms); the card counts down to it. */
    readonly endsAt?: Date | number | undefined;
    /** How `endsAt`'s time left is written; `formatTimeLeft` by default. */
    readonly formatCountdown?: ((ms: number) => string) | undefined;
    /** Called once when `endsAt` passes. */
    readonly onend?: (() => void) | undefined;
    /** The clock to count with (a test's); `Date.now` by default. */
    readonly now?: (() => number) | undefined;
    /** What the countdown means, for a screen reader ("Ends in"). */
    readonly countdownLabel?: string | undefined;
    readonly children?: Snippet | undefined;
    readonly actions?: Snippet | undefined;
    readonly testid?: string | undefined;
    readonly countdownTestid?: string | undefined;
  }
  const {
    title,
    detail,
    icon,
    iconBg,
    iconInk,
    countdown,
    endsAt,
    formatCountdown = formatTimeLeft,
    onend,
    now = Date.now,
    countdownLabel,
    children,
    actions,
    testid,
    countdownTestid = 'ui-challenge-countdown',
  }: Props = $props();

  let nowMs = $state(0);

  // The card's own clock, only while `endsAt` is set and nothing else says
  // the time: one timeout at a time, aimed at the next moment the text changes.
  $effect(() => {
    if (endsAt === undefined || countdown !== undefined) return undefined;
    const end = typeof endsAt === 'number' ? endsAt : endsAt.getTime();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ended = false;
    const tick = (): void => {
      // A local, not the state: reading `nowMs` here would make this
      // effect depend on the clock it sets, and re-arm on every tick.
      const at = now();
      nowMs = at;
      const left = end - at;
      if (left <= 0) {
        if (!ended) {
          ended = true;
          onend?.();
        }
        return;
      }
      timer = setTimeout(tick, nextTickIn(left));
    };
    tick();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  });

  const shown = $derived.by(() => {
    if (countdown !== undefined) return countdown;
    if (endsAt === undefined) return undefined;
    const end = typeof endsAt === 'number' ? endsAt : endsAt.getTime();
    return formatCountdown(Math.max(0, end - nowMs));
  });

  const titleId = $props.id();
</script>

<section class="ui-challenge" aria-labelledby={titleId} data-testid={testid}>
  <div class="ui-challenge-head">
    <span
      class="ui-challenge-icon"
      use:styleProps={{ background: iconBg, color: iconInk }}
      aria-hidden="true">{@render icon()}</span
    >
    <span class="ui-challenge-name">
      <h2 id={titleId} class="ui-challenge-title">{title}</h2>
      {#if detail !== undefined}
        <span class="ui-challenge-detail">{detail}</span>
      {/if}
    </span>
    {#if shown !== undefined && shown.length > 0}
      <span class="ui-challenge-clock" data-testid={countdownTestid}>
        {#if countdownLabel !== undefined}
          <span class="ui-visually-hidden">{countdownLabel}</span>
        {/if}
        {shown}
      </span>
    {/if}
  </div>
  {#if children !== undefined}
    <div class="ui-challenge-body">{@render children()}</div>
  {/if}
  {#if actions !== undefined}
    <div class="ui-challenge-actions">{@render actions()}</div>
  {/if}
</section>

<style>
  .ui-challenge {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
    border-radius: var(--ui-radius-lg);
    border: 1px solid var(--ui-line);
    padding: var(--ui-space-4);
    background: var(--ui-card);
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-challenge-head {
    display: flex;
    align-items: center;
    gap: var(--ui-space-3);
  }

  .ui-challenge-icon {
    flex: 0 0 auto;
    width: 2.6rem;
    height: 2.6rem;
    border-radius: var(--ui-radius-md);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--ui-accent);
    color: var(--ui-on-accent);
  }

  .ui-challenge-icon :global(svg) {
    width: 1.35rem;
    height: 1.35rem;
  }

  .ui-challenge-name {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-challenge-title {
    margin: 0;
    font: var(--ui-text-heading);
    color: var(--ui-ink);
  }

  .ui-challenge-detail {
    font: var(--ui-text-caption);
    font-weight: 700;
    color: var(--ui-ink-2);
  }

  .ui-challenge-clock {
    flex: 0 0 auto;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    background: var(--ui-surface-2);
    color: var(--ui-ink);
    font: var(--ui-text-caption);
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .ui-challenge-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ui-space-2);
  }

  .ui-visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
